export const generateRandomColor = () => {
    const letters = '0123456789ABCDEF';
    let color = '#';
    for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
    }
    return color;
};

/**
 * Calculates the relative luminance of a color
 * @param {string} hexColor - Hex color string (e.g., '#RRGGBB' or '#RGB')
 * @returns {number} Luminance value between 0 and 1
 */
const getColorLuminance = (hexColor) => {
    // Remove the hash if present
    const hex = hexColor.replace('#', '');

    // Convert 3-digit hex to 6-digit
    const fullHex =
        hex.length === 3
            ? hex
                  .split('')
                  .map((char) => char + char)
                  .join('')
            : hex;

    // Parse RGB values
    const r = parseInt(fullHex.substring(0, 2), 16) / 255;
    const g = parseInt(fullHex.substring(2, 4), 16) / 255;
    const b = parseInt(fullHex.substring(4, 6), 16) / 255;

    // Apply gamma correction
    const [rs, gs, bs] = [r, g, b].map((c) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)));

    // Calculate relative luminance using ITU-R BT.709 coefficients
    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

/**
 * Calculates the WCAG contrast ratio between two luminance values
 * @param {number} l1 - Luminance of the first color
 * @param {number} l2 - Luminance of the second color
 * @returns {number} Contrast ratio (1 to 21)
 */
const getContrastRatio = (l1, l2) => {
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    return (lighter + 0.05) / (darker + 0.05);
};

/**
 * Determines whether white or black text should be used on a given background color
 * Uses WCAG 2.1 AA compliant contrast ratio calculation (minimum 4.5:1)
 * @param {string} backgroundColor - Hex color string (e.g., '#RRGGBB')
 * @returns {string} '#ffffff' for white text or '#000000' for black text
 */
export const getContrastingTextColor = (backgroundColor) => {
    if (!backgroundColor) return '#ffffff';

    const bgLuminance = getColorLuminance(backgroundColor);

    // White has luminance of 1, black has luminance of 0
    const contrastWithWhite = getContrastRatio(bgLuminance, 1);
    const contrastWithBlack = getContrastRatio(bgLuminance, 0);

    // Return the color that provides better contrast (WCAG AA requires 4.5:1 minimum)
    return contrastWithWhite >= contrastWithBlack ? '#ffffff' : '#000000';
};
